import { Checkbox } from 'lo-bahamut';

export type PcpSpecialtyRule = {
  aliases: string[];
  fallback: string;
};

export type SpecialtyDropdownRef = {
  data?: string[];
  checkboxes?: Checkbox[];
  hide?: boolean;
  disabled?: boolean;
  jsonid?: string;
  clearComponent?: () => void;
  cdRef?: { detectChanges?: () => void };
};

export type FilterPillRef = {
  data?: unknown[];
  hide?: boolean;
  label?: string;
};

type GmListEntry = {
  reference?: SpecialtyDropdownRef & FilterPillRef;
};

export const PCP_SPECIALTY_RULES: ReadonlyArray<PcpSpecialtyRule> = [
  { aliases: ['family medicine'], fallback: 'family medicine' },
  {
    aliases: ['federally qualified health center', 'federally qualified health center fqhc', 'clinic fqhc'],
    fallback: 'federally qualified health center',
  },
  { aliases: ['geriatric medicine', 'geriatrics'], fallback: 'geriatric medicine' },
  { aliases: ['general practice'], fallback: 'general practice' },
  { aliases: ['internal medicine'], fallback: 'internal medicine' },
];

// Finds component refs by jsonid in a defensive way to support tenant-specific layouts.
export function getRefsByJsonId(
  gmList: Record<string, GmListEntry> | undefined,
  dropdownIds: string[]
): Array<SpecialtyDropdownRef & FilterPillRef> {
  const entries = Object.values(gmList ?? {});
  return dropdownIds
    .map((id) => entries.find((entry) => entry?.reference?.jsonid === id)?.reference)
    .filter((ref): ref is SpecialtyDropdownRef & FilterPillRef => Boolean(ref));
}

export function getActiveVisibleEnabledRef<T extends { hide?: boolean; disabled?: boolean }>(
  refs: T[]
): T | undefined {
  return refs.find((ref) => !ref.hide && !ref.disabled) ?? refs[0];
}

export function hideFilterPill(ref: FilterPillRef | undefined): void {
  if (!ref) {
    return;
  }
  ref.hide = true;
  ref.label = '';
  if (Array.isArray(ref.data)) {
    ref.data = [];
  }
}

export function applyPcpSpecialtySelection({
  isChecked,
  dropdownRef,
  currentValues,
  rules = PCP_SPECIALTY_RULES,
}: {
  isChecked: boolean;
  dropdownRef?: SpecialtyDropdownRef;
  currentValues?: string[];
  rules?: ReadonlyArray<PcpSpecialtyRule>;
}): string[] {
  // Use tenant-provided dropdown values when available; otherwise rely on stable fallbacks.
  const pcpSpecialtyValues = dropdownRef
    ? resolvePcpSpecialtyValues(dropdownRef, rules)
    : rules.map((rule) => rule.fallback);

  const dropdownData = dropdownRef?.data;
  const sourceValues = Array.isArray(dropdownData)
    ? dropdownData
    : (Array.isArray(currentValues) ? currentValues : []);

  const nextValues = isChecked
    ? Array.from(new Set([...sourceValues, ...pcpSpecialtyValues]))
    : sourceValues.filter((value) => !isPcpSpecialtyValue(value, pcpSpecialtyValues, rules));

  updateDropdownSelection(dropdownRef, nextValues);
  return nextValues;
}

function resolvePcpSpecialtyValues(
  dropdownRef: SpecialtyDropdownRef,
  rules: ReadonlyArray<PcpSpecialtyRule>
): string[] {
  const checkboxes = Array.isArray(dropdownRef?.checkboxes) ? dropdownRef.checkboxes : [];
  const values = rules.map((rule) => {
    const match = checkboxes.find((checkbox) =>
      rule.aliases.some((alias) =>
        matchesSpecialtyAlias(
          `${checkbox?.label ?? ''} ${typeof checkbox?.value === 'string' ? checkbox.value : ''}`,
          alias
        )
      )
    );
    return typeof match?.value === 'string' ? match.value : rule.fallback;
  });

  return Array.from(new Set(values));
}

function updateDropdownSelection(dropdownRef: SpecialtyDropdownRef | undefined, selectedValues: string[]): void {
  if (!dropdownRef) {
    return;
  }

  if (typeof dropdownRef.clearComponent === 'function') {
    dropdownRef.clearComponent();
  }
  dropdownRef.data = selectedValues;

  if (Array.isArray(dropdownRef.checkboxes)) {
    dropdownRef.checkboxes.forEach((checkbox) => {
      checkbox.checked = typeof checkbox.value === 'string' && selectedValues.includes(checkbox.value);
    });
  }

  if (dropdownRef?.cdRef?.detectChanges) {
    dropdownRef.cdRef.detectChanges();
  }
}

function isPcpSpecialtyValue(
  value: string,
  resolvedPcpValues: string[],
  rules: ReadonlyArray<PcpSpecialtyRule>
): boolean {
  const normalizedValue = normalizeSpecialtyText(value);
  if (!normalizedValue) {
    return false;
  }

  const inResolvedValues = resolvedPcpValues.some(
    (pcpValue) => normalizeSpecialtyText(pcpValue) === normalizedValue
  );
  if (inResolvedValues) {
    return true;
  }

  // Keep this alias-based fallback so we can remove PCP-added specialties consistently
  // even when dropdown value text differs slightly across tenant configurations.
  return rules.some((rule) => rule.aliases.some((alias) => matchesSpecialtyAlias(value, alias)));
}

function matchesSpecialtyAlias(candidate: string, alias: string): boolean {
  const normalizedCandidate = normalizeSpecialtyText(candidate);
  const normalizedAlias = normalizeSpecialtyText(alias);
  if (!normalizedCandidate || !normalizedAlias) {
    return false;
  }

  return (
    normalizedCandidate === normalizedAlias ||
    normalizedCandidate.includes(normalizedAlias) ||
    normalizedAlias.includes(normalizedCandidate)
  );
}

function normalizeSpecialtyText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}
